import React, { useState, useMemo, useRef, useEffect, memo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../Utils/Axios';
import UrduKeyboard from '../../Components/UrduKeyboard';

const SalesTab = memo(function SalesTab({ sales, companyLanguage, handleEditSale, handleDeleteSale, bills = [], company = null }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchUrduKeyboard, setShowSearchUrduKeyboard] = useState(false);
  const searchInputRef = useRef(null);
  // Set today's date (local) as default for date filters
  const today = new Date().toLocaleDateString('en-CA');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterSeller, setFilterSeller] = useState('');
  const [filterAmountMin, setFilterAmountMin] = useState('');
  const [filterAmountMax, setFilterAmountMax] = useState('');
  const [showFilters, setShowFilters] = useState(true); // Auto-open filters
  const [selectedSales, setSelectedSales] = useState([]);
  const [showGeneralSales, setShowGeneralSales] = useState(false);
  const [generalSales, setGeneralSales] = useState([]);
  const [isLoadingGeneralSales, setIsLoadingGeneralSales] = useState(false);
  const [activeViewTab, setActiveViewTab] = useState('normal'); // 'normal', 'billwise', 'supplierwise', or 'customerwise'
  const [isProcessing, setIsProcessing] = useState(false);
  const printRef = useRef(null);

  const fetchGeneralSales = async () => {
    setIsLoadingGeneralSales(true);
    try {
      const token = localStorage.getItem('companyToken');
      if (!token) return;

      const response = await api.get('/ledger/general-sales', {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('General sales API response:', response.data);
      console.log('General sales array:', response.data?.generalSales);

      // Transform general sales to match regular sales format
      const transformedGeneralSales = (response.data?.generalSales || []).map(gs => {
        console.log('Processing general sale:', gs);
        return {
          _id: gs._id,
          serialNumber: gs._id?.slice(-6) || 'N/A',
          date: gs.date || new Date().toLocaleDateString('en-CA'),
          supplierName: gs.partyId?.name || '',
          supplierNameUrdu: gs.partyId?.nameUrdu || '',
          sellerName: '',
          sellerNameUrdu: '',
          totalAmount: gs.amount || 0,
          isGeneralSale: true // Flag to identify general sales
        };
      });

      console.log('Transformed general sales:', transformedGeneralSales);
      console.log('Transformed general sales count:', transformedGeneralSales.length);
      setGeneralSales(transformedGeneralSales);
    } catch (error) {
      console.error('Fetch general sales error:', error);
      toast.error(companyLanguage === 'ur' ? 'جنرل سیلز لوڈ کرنے میں ناکام' : 'Failed to load general sales');
    } finally {
      setIsLoadingGeneralSales(false);
    }
  };

  // Fetch General Sales when toggle is enabled
  useEffect(() => {
    if (showGeneralSales) {
      fetchGeneralSales();
    } else {
      // Clear general sales when toggle is off
      setGeneralSales([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGeneralSales]);

  // Auto-close Urdu keyboard when English character is typed in searchTerm
  // Auto-open when field becomes empty
  useEffect(() => {
    if (searchTerm === undefined || searchTerm === null) return;

    const value = String(searchTerm);

    // Check if field contains any English characters
    const hasEnglishChar = /[a-zA-Z]/.test(value);

    if (hasEnglishChar) {
      // Close Urdu keyboard if English character is found
      setShowSearchUrduKeyboard(false);
    } else if (value.trim() === '' && showSearchUrduKeyboard === false) {
      // Open Urdu keyboard if field becomes empty and keyboard is not already open
      // Only open if the field is focused (to avoid opening when form is reset)
      if (searchInputRef.current && searchInputRef.current === document.activeElement) {
        setShowSearchUrduKeyboard(true);
      }
    }
  }, [searchTerm, showSearchUrduKeyboard]);

  // Handle auto-filter from URL search parameters (e.g., from Day Book)
  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      setFilterDateFrom(dateParam);
      setFilterDateTo(dateParam);
      setShowFilters(true);
    }
  }, [searchParams]);

  // Combine regular sales and general sales - Optimized with useMemo
  const allSales = useMemo(() => {
    // Use requestIdleCallback for non-critical processing if available
    if (showGeneralSales && generalSales.length > 0) {
      return [...sales, ...generalSales];
    }
    return sales;
  }, [sales, generalSales, showGeneralSales]);

  // Get unique suppliers and sellers for filter dropdowns - Optimized
  const uniqueSuppliers = useMemo(() => {
    if (!allSales || allSales.length === 0) return [];
    const suppliers = new Set();
    // Use for loop for better performance than forEach
    for (let i = 0; i < allSales.length; i++) {
      const sale = allSales[i];
      const name = sale.supplierNameUrdu || sale.supplierName;
      if (name) suppliers.add(name);
    }
    return Array.from(suppliers).sort();
  }, [allSales]);

  const uniqueSellers = useMemo(() => {
    if (!allSales || allSales.length === 0) return [];
    const sellers = new Set();
    // Use for loop for better performance than forEach
    for (let i = 0; i < allSales.length; i++) {
      const sale = allSales[i];
      const name = sale.sellerNameUrdu || sale.sellerName;
      if (name) sellers.add(name);
    }
    return Array.from(sellers).sort();
  }, [allSales]);

  // Filter sales based on all filter criteria - Optimized
  const filteredSales = useMemo(() => {
    if (!allSales || allSales.length === 0) return [];

    // Early return if no filters applied
    const hasFilters = searchTerm || filterDateFrom || filterDateTo || filterSupplier || filterSeller || filterAmountMin || filterAmountMax;
    if (!hasFilters) return allSales;

    return allSales.filter(sale => {
      // Search term filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const supplierName = (sale.supplierNameUrdu || sale.supplierName || '').toLowerCase();
        const sellerName = (sale.sellerNameUrdu || sale.sellerName || '').toLowerCase();
        const serialNumber = (sale.serialNumber || '').toString().toLowerCase();

        if (!supplierName.includes(searchLower) &&
          !sellerName.includes(searchLower) &&
          !serialNumber.includes(searchLower)) {
          return false;
        }
      }

      // Date range filter
      if (filterDateFrom && sale.date) {
        const saleDate = new Date(sale.date);
        const fromDate = new Date(filterDateFrom);
        fromDate.setHours(0, 0, 0, 0);
        saleDate.setHours(0, 0, 0, 0);
        if (saleDate < fromDate) return false;
      }
      if (filterDateTo && sale.date) {
        const saleDate = new Date(sale.date);
        const toDate = new Date(filterDateTo);
        toDate.setHours(23, 59, 59, 999);
        saleDate.setHours(0, 0, 0, 0);
        if (saleDate > toDate) return false;
      }

      // Supplier filter
      if (filterSupplier) {
        const supplierName = sale.supplierNameUrdu || sale.supplierName || '';
        if (supplierName !== filterSupplier) return false;
      }

      // Seller filter
      if (filterSeller) {
        const sellerName = sale.sellerNameUrdu || sale.sellerName || '';
        if (sellerName !== filterSeller) return false;
      }

      // Amount range filter
      const amount = sale.totalAmount || 0;
      if (filterAmountMin) {
        if (amount < parseFloat(filterAmountMin)) return false;
      }
      if (filterAmountMax) {
        if (amount > parseFloat(filterAmountMax)) return false;
      }

      return true;
    });
  }, [allSales, searchTerm, filterDateFrom, filterDateTo, filterSupplier, filterSeller, filterAmountMin, filterAmountMax]);

  // Group filtered sales by date and calculate totals (for normal view) - Optimized
  const salesByDate = useMemo(() => {
    if (!filteredSales || !Array.isArray(filteredSales) || filteredSales.length === 0) {
      return [];
    }
    const grouped = {};
    // Use for loop for better performance
    for (let i = 0; i < filteredSales.length; i++) {
      const sale = filteredSales[i];
      if (!sale || !sale.date) continue;
      const dateKey = new Date(sale.date).toLocaleDateString('en-CA');
      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          date: dateKey,
          sales: [],
          total: 0
        };
      }
      grouped[dateKey].sales.push(sale);
      // Parse totalAmount as number to ensure proper calculation
      const amount = parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0;
      grouped[dateKey].total += amount;
    }

    // Convert to array and sort by date (newest first)
    return Object.values(grouped).sort((a, b) => {
      return new Date(b.date) - new Date(a.date);
    });
  }, [filteredSales]);

  // Calculate pukhta bikri (net sale) for a bill - same logic as BillsTab
  const calculatePukhtaBikri = (bill) => {
    if (!bill) return 0;
    // Get raw sale (خام بکری)
    const rawSale = bill.sales?.rawSale || bill.sales?.['Raw Sale'] || bill.expenses?.['خام بکری'] || bill.rawSale || 0;
    const rawSaleNum = typeof rawSale === 'string' ? parseFloat(rawSale) || 0 : (rawSale || 0);

    // Calculate total expenses - same logic as BillsTab (exclude non-expense fields and laga)
    let totalExpenses = 0;
    if (bill.expenses && typeof bill.expenses === 'object') {
      const expensesObj = bill.expenses instanceof Map ? Object.fromEntries(bill.expenses) : bill.expenses;
      Object.keys(expensesObj).forEach(key => {
        const value = expensesObj[key];
        // Skip non-expense fields: Vehicle, ItemName, ItemUnit, ItemQuantity, ActualVehicle, calculated fields, and laga
        const isNonExpenseField =
          key === 'Vehicle' ||
          key === 'ActualVehicle' ||
          key === 'ItemName' ||
          key === 'ItemUnit' ||
          key === 'ItemQuantity' ||
          key === 'خام بکری' ||
          key === 'اخراجات' ||
          key === 'پختہ بکری' ||
          key === 'لاگا' || // Exclude laga from expenses (it goes to amdani only)
          key === 'Laga' ||
          key === 'DeliveryMethod' ||
          key === 'GoodsMessageUrdu';

        if (!isNonExpenseField) {
          const numValue = typeof value === 'string' ? parseFloat(value) || 0 : (typeof value === 'number' && !isNaN(value) ? value : 0);
          totalExpenses += numValue;
        }
      });
    }

    // Calculate net sale (پختہ بکری) = rawSale - totalExpenses
    const netSale = rawSaleNum - totalExpenses;
    return netSale;
  };

  // Group filtered sales by bill (serialNumber) and calculate totals
  const salesByBill = useMemo(() => {
    const grouped = {};

    filteredSales.forEach(sale => {
      // Skip general sales or sales without serialNumber
      if (sale.isGeneralSale || !sale.serialNumber) {
        // Group general sales separately
        const generalKey = 'general-sales';
        if (!grouped[generalKey]) {
          grouped[generalKey] = {
            serialNumber: null,
            billNumber: companyLanguage === 'ur' ? 'جنرل سیلز' : 'General Sales',
            supplierName: '',
            supplierNameUrdu: '',
            pukhtaBikri: 0,
            sales: [],
            total: 0,
            totalRemaining: 0
          };
        }
        grouped[generalKey].sales.push(sale);
        const amount = parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0;
        const paidAmount = parseFloat(sale.amountPaid) || 0;
        const remainingAmount = amount - paidAmount;

        grouped[generalKey].total += amount;
        grouped[generalKey].totalRemaining += remainingAmount;
        return;
      }

      const serialNumber = String(sale.serialNumber).trim();
      if (!grouped[serialNumber]) {
        // Find matching bill
        const matchingBill = bills.find(bill => {
          const billSerialNumber = bill.serialNumber ? String(bill.serialNumber).trim() : '';
          return billSerialNumber === serialNumber;
        });

        grouped[serialNumber] = {
          serialNumber: serialNumber,
          billNumber: matchingBill ? (matchingBill.serialNumber || serialNumber) : serialNumber,
          supplierName: matchingBill ? (matchingBill.customerName || '') : (sale.supplierName || ''),
          supplierNameUrdu: matchingBill ? (matchingBill.customerNameUrdu || '') : (sale.supplierNameUrdu || ''),
          pukhtaBikri: matchingBill ? calculatePukhtaBikri(matchingBill) : 0,
          sales: [],
          total: 0,
          totalRemaining: 0
        };
      }

      grouped[serialNumber].sales.push(sale);
      // Parse totalAmount as number to ensure proper calculation
      const amount = parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0;
      const paidAmount = parseFloat(sale.amountPaid) || 0;
      const remainingAmount = amount - paidAmount;

      grouped[serialNumber].total += amount;
      grouped[serialNumber].totalRemaining += remainingAmount;
    });

    // Convert to array and sort by serialNumber (newest first, assuming higher serialNumber = newer)
    return Object.values(grouped).sort((a, b) => {
      if (a.serialNumber === null) return 1; // General sales at the end
      if (b.serialNumber === null) return -1;
      const aNum = parseInt(a.serialNumber, 10) || 0;
      const bNum = parseInt(b.serialNumber, 10) || 0;
      return bNum - aNum; // Descending order
    });
  }, [filteredSales, bills, companyLanguage]);

  // Group filtered sales by supplier, then by bill (date-wise)
  const salesBySupplier = useMemo(() => {
    if (!filteredSales || !Array.isArray(filteredSales)) {
      return [];
    }

    const supplierGroups = {};

    filteredSales.forEach(sale => {
      // Skip general sales
      if (sale.isGeneralSale) {
        return;
      }

      // Get supplier name (from sale or from matching bill)
      let supplierName = sale.supplierNameUrdu || sale.supplierName || '';
      let supplierNameEn = sale.supplierName || '';

      // If sale has serialNumber, try to find matching bill for supplier info
      if (sale.serialNumber) {
        const matchingBill = bills.find(bill => {
          const billSerialNumber = bill.serialNumber ? String(bill.serialNumber).trim() : '';
          return billSerialNumber === String(sale.serialNumber).trim();
        });

        if (matchingBill) {
          supplierName = matchingBill.customerNameUrdu || matchingBill.customerName || supplierName;
          supplierNameEn = matchingBill.customerName || supplierNameEn;
        }
      }

      // Use supplier name as key, fallback to 'Unknown'
      const supplierKey = supplierName || supplierNameEn || 'Unknown';

      if (!supplierGroups[supplierKey]) {
        supplierGroups[supplierKey] = {
          supplierName: supplierNameEn,
          supplierNameUrdu: supplierName,
          bills: {}
        };
      }

      // Group by bill (serialNumber) and date
      const billKey = sale.serialNumber ? String(sale.serialNumber).trim() : sale.date || 'no-bill';
      const saleDate = sale.date ? new Date(sale.date).toLocaleDateString('en-CA') : 'no-date';

      if (!supplierGroups[supplierKey].bills[billKey]) {
        // Find matching bill for bill info
        const matchingBill = sale.serialNumber ? bills.find(bill => {
          const billSerialNumber = bill.serialNumber ? String(bill.serialNumber).trim() : '';
          return billSerialNumber === billKey;
        }) : null;

        supplierGroups[supplierKey].bills[billKey] = {
          serialNumber: sale.serialNumber || null,
          billNumber: matchingBill ? (matchingBill.serialNumber || billKey) : billKey,
          date: saleDate,
          pukhtaBikri: matchingBill ? calculatePukhtaBikri(matchingBill) : 0,
          sales: [],
          total: 0,
          totalRemaining: 0
        };
      }

      supplierGroups[supplierKey].bills[billKey].sales.push(sale);
      const amount = parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0;
      const paidAmount = parseFloat(sale.amountPaid) || 0;
      const remainingAmount = amount - paidAmount;

      supplierGroups[supplierKey].bills[billKey].total += amount;
      supplierGroups[supplierKey].bills[billKey].totalRemaining += remainingAmount;
    });

    // Convert to array and sort suppliers alphabetically
    // Then sort bills by date (newest first)
    return Object.values(supplierGroups).map(supplier => ({
      ...supplier,
      bills: Object.values(supplier.bills).sort((a, b) => {
        return new Date(b.date) - new Date(a.date);
      })
    })).sort((a, b) => {
      const nameA = (a.supplierNameUrdu || a.supplierName || '').toLowerCase();
      const nameB = (b.supplierNameUrdu || b.supplierName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [filteredSales, bills]);

  // Group filtered sales by customer (seller), then by bill (date-wise)
  const salesByCustomer = useMemo(() => {
    if (!filteredSales || !Array.isArray(filteredSales)) {
      return [];
    }

    const customerGroups = {};

    filteredSales.forEach(sale => {
      // Skip general sales
      if (sale.isGeneralSale) {
        return;
      }

      // Get customer name (seller name)
      const customerName = sale.sellerNameUrdu || sale.sellerName || '';
      const customerNameEn = sale.sellerName || '';

      // Use customer name as key, fallback to 'Unknown'
      const customerKey = customerName || customerNameEn || 'Unknown';

      if (!customerGroups[customerKey]) {
        customerGroups[customerKey] = {
          customerName: customerNameEn,
          customerNameUrdu: customerName,
          bills: {}
        };
      }

      // Group by bill (serialNumber) and date
      const billKey = sale.serialNumber ? String(sale.serialNumber).trim() : sale.date || 'no-bill';
      const saleDate = sale.date ? new Date(sale.date).toLocaleDateString('en-CA') : 'no-date';

      if (!customerGroups[customerKey].bills[billKey]) {
        // Find matching bill for bill info
        const matchingBill = sale.serialNumber ? bills.find(bill => {
          const billSerialNumber = bill.serialNumber ? String(bill.serialNumber).trim() : '';
          return billSerialNumber === billKey;
        }) : null;

        customerGroups[customerKey].bills[billKey] = {
          serialNumber: sale.serialNumber || null,
          billNumber: matchingBill ? (matchingBill.serialNumber || billKey) : billKey,
          date: saleDate,
          pukhtaBikri: matchingBill ? calculatePukhtaBikri(matchingBill) : 0,
          sales: [],
          total: 0,
          totalRemaining: 0
        };
      }

      customerGroups[customerKey].bills[billKey].sales.push(sale);
      const amount = parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0;
      const paidAmount = parseFloat(sale.amountPaid) || 0;
      const remainingAmount = amount - paidAmount;

      customerGroups[customerKey].bills[billKey].total += amount;
      customerGroups[customerKey].bills[billKey].totalRemaining += remainingAmount;
    });

    // Convert to array and sort customers alphabetically
    // Then sort bills by date (newest first)
    return Object.values(customerGroups).map(customer => ({
      ...customer,
      bills: Object.values(customer.bills).sort((a, b) => {
        return new Date(b.date) - new Date(a.date);
      })
    })).sort((a, b) => {
      const nameA = (a.customerNameUrdu || a.customerName || '').toLowerCase();
      const nameB = (b.customerNameUrdu || b.customerName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [filteredSales, bills]);

  // Calculate overall total from all filtered sales
  const overallTotal = useMemo(() => {
    return filteredSales.reduce((sum, sale) => {
      const amount = parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0;
      return sum + amount;
    }, 0);
  }, [filteredSales]);

  // Handle Urdu keyboard input for search field
  const handleSearchUrduKeyPress = (key) => {
    if (key === 'backspace') {
      setSearchTerm((prev) => prev ? prev.slice(0, -1) : '');
    } else {
      setSearchTerm((prev) => (prev || '') + key);
    }
  };

  // Toggle Urdu keyboard on search input click
  // Open keyboard if field is empty, otherwise toggle
  const handleSearchInputClick = () => {
    if (!searchTerm || searchTerm.trim() === '') {
      // If field is empty, open keyboard
      setShowSearchUrduKeyboard(true);
    } else {
      // Otherwise toggle
      setShowSearchUrduKeyboard(prev => !prev);
    }
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterSupplier('');
    setFilterSeller('');
    setFilterAmountMin('');
    setFilterAmountMax('');
  };

  // Check if any filter is active
  const hasActiveFilters = searchTerm || filterDateFrom || filterDateTo || filterSupplier || filterSeller || filterAmountMin || filterAmountMax;

  // Select All functionality
  const handleSelectAll = () => {
    const allFilteredIds = filteredSales.map(sale => sale._id);
    const allSelected = allFilteredIds.every(id => selectedSales.includes(id));

    if (allSelected) {
      // Deselect all filtered sales
      setSelectedSales(prev => prev.filter(id => !allFilteredIds.includes(id)));
    } else {
      // Select all filtered sales
      setSelectedSales(prev => {
        const newSelection = [...prev];
        allFilteredIds.forEach(id => {
          if (!newSelection.includes(id)) {
            newSelection.push(id);
          }
        });
        return newSelection;
      });
    }
  };

  const handleToggleSale = (saleId) => {
    setSelectedSales(prev => {
      if (prev.includes(saleId)) {
        return prev.filter(id => id !== saleId);
      } else {
        return [...prev, saleId];
      }
    });
  };

  const isAllSelected = filteredSales.length > 0 && filteredSales.every(sale => selectedSales.includes(sale._id));

  // Bulk Delete functionality
  const handleBulkDelete = async () => {
    if (selectedSales.length === 0) return;

    const confirmMessage = companyLanguage === 'ur'
      ? `کیا آپ واقعی ${selectedSales.length} سیلز ختم کرنا چاہتے ہیں؟`
      : `Are you sure you want to delete ${selectedSales.length} sales?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      const token = localStorage.getItem('companyToken');
      if (!token) {
        toast.error(companyLanguage === 'ur' ? 'لاگ ان کریں' : 'Please login');
        return;
      }

      let successCount = 0;
      let failCount = 0;

      // Delete all selected sales in parallel
      const deletePromises = selectedSales.map(async (saleId) => {
        try {
          // Check if it's a general sale
          const sale = allSales.find(s => s._id === saleId);
          if (sale?.isGeneralSale) {
            // Delete general sale (ledger transaction)
            await api.delete(`/ledger/transactions/${saleId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } else {
            // Delete regular sale
            await api.delete(`/sale/company/${saleId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          }
          return { success: true, saleId };
        } catch (error) {
          console.error(`Failed to delete sale ${saleId}:`, error);
          return { success: false, saleId };
        }
      });

      const results = await Promise.all(deletePromises);
      successCount = results.filter(r => r.success).length;
      failCount = results.filter(r => !r.success).length;

      setSelectedSales([]);

      if (successCount > 0) {
        toast.success(
          companyLanguage === 'ur'
            ? `${successCount} سیلز کامیابی سے ختم ہو گئے`
            : `${successCount} sales deleted successfully`
        );
        // Refresh sales list
        if (window.location.reload) {
          setTimeout(() => window.location.reload(), 500);
        }

        // Refresh general sales if enabled
        if (showGeneralSales) {
          fetchGeneralSales();
        }
      }

      if (failCount > 0) {
        toast.error(
          companyLanguage === 'ur'
            ? `${failCount} سیلز ختم کرنے میں ناکام`
            : `Failed to delete ${failCount} sales`
        );
      }
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error(companyLanguage === 'ur' ? 'ختم کرنے میں ناکام' : 'Failed to delete sales');
    }
  };

  // Show loading state if processing large dataset
  const isProcessingData = useMemo(() => {
    return allSales.length > 1000 && filteredSales.length !== allSales.length;
  }, [allSales.length, filteredSales.length]);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 border border-brand-card animate-fade-in-up">
      {isProcessingData && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className={`text-sm text-blue-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
            {companyLanguage === 'ur' ? 'ڈیٹا پروسیس ہو رہا ہے...' : 'Processing data...'}
          </p>
        </div>
      )}
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <h2 className={`text-2xl font-bold text-brand-header ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
          {companyLanguage === 'ur' ? 'سیلز' : 'Sales'}
          <span className="text-lg text-gray-500 ml-2">
            ({filteredSales.length}/{allSales.length})
          </span>
        </h2>

        {/* View Tabs */}
        <div className="flex gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveViewTab('normal')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${activeViewTab === 'normal'
              ? 'text-brand-header border-b-2 border-brand-header'
              : 'text-gray-500 hover:text-gray-700'
              } ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
          >
            {companyLanguage === 'ur' ? 'عام سیل فہرست' : 'Normal Sale List'}
          </button>
          <button
            onClick={() => setActiveViewTab('billwise')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${activeViewTab === 'billwise'
              ? 'text-brand-header border-b-2 border-brand-header'
              : 'text-gray-500 hover:text-gray-700'
              } ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
          >
            {companyLanguage === 'ur' ? 'بل وائز سیل' : 'Bill Wise Sale'}
          </button>
          <button
            onClick={() => setActiveViewTab('supplierwise')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${activeViewTab === 'supplierwise'
              ? 'text-brand-header border-b-2 border-brand-header'
              : 'text-gray-500 hover:text-gray-700'
              } ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
          >
            {companyLanguage === 'ur' ? 'سپلائر وائز' : 'Supplier Wise'}
          </button>
          <button
            onClick={() => setActiveViewTab('customerwise')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${activeViewTab === 'customerwise'
              ? 'text-brand-header border-b-2 border-brand-header'
              : 'text-gray-500 hover:text-gray-700'
              } ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
          >
            {companyLanguage === 'ur' ? 'کسٹمر وائز' : 'Customer Wise'}
          </button>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 transform hover:scale-105 ${showFilters || hasActiveFilters
              ? 'bg-green-500 hover:bg-green-600 text-white'
              : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
              }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {companyLanguage === 'ur' ? 'فلٹر' : 'Filter'}
            {hasActiveFilters && (
              <span className="bg-white text-green-500 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                !
              </span>
            )}
          </button>
          <button
            onClick={() => navigate(companyLanguage === 'ur' ? '/sale/create' : '/sale/create?lang=en')}
            className="bg-brand-header hover:bg-emerald-800 text-white font-semibold px-4 py-2 rounded-lg transition-all flex items-center gap-2 transform hover:scale-105 hover:shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {companyLanguage === 'ur' ? 'نیا سیل' : 'Create New Sale'}
          </button>
          <button
            onClick={() => navigate('/general-sale')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg transition-all flex items-center gap-2 transform hover:scale-105 hover:shadow-lg"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {companyLanguage === 'ur' ? 'جنرل سیل' : 'General Sale'}
          </button>
        </div>
      </div>

      {/* Filter Section */}
      {showFilters && (
        <div className="mb-2 p-1.5 bg-gradient-to-r from-green-50 to-lime-50 rounded-xl border border-green-200 animate-fade-in-down shadow-lg print:hidden">
          <div className="flex justify-between items-center mb-1">
            <h3 className={`font-bold text-green-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
              {companyLanguage === 'ur' ? 'فلٹرز' : 'Filters'}
            </h3>
            <div className="flex gap-2">
              {hasActiveFilters && (
                <button
                  onClick={resetFilters}
                  className="px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white text-xs rounded-lg transition-all"
                >
                  {companyLanguage === 'ur' ? 'ری سیٹ' : 'Reset'}
                </button>
              )}
              <button
                onClick={() => setShowFilters(false)}
                className="px-2 py-1 bg-gray-300 hover:bg-gray-400 text-gray-700 text-xs rounded-lg transition-all"
              >
                {companyLanguage === 'ur' ? 'بند کریں' : 'Close'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-1.5">
            {/* Search */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'تلاش:' : 'Search:'}
              </label>
              <input
                type="text"
                ref={searchInputRef}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={handleSearchInputClick}
                onFocus={() => {
                  // Open Urdu keyboard on focus if field is empty
                  if (!searchTerm || searchTerm.trim() === '') {
                    setShowSearchUrduKeyboard(true);
                  }
                }}
                placeholder={companyLanguage === 'ur' ? 'سپلائر، بیچنے والا، نمبر...' : 'Supplier, Seller, Number...'}
                className={`w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs ${companyLanguage === 'ur' ? 'font-urdu text-right' : ''}`}
                style={companyLanguage === 'ur' ? {
                  fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif",
                  lineHeight: '1.8',
                  paddingTop: '6px',
                  paddingBottom: '6px',
                  fontSize: '12px',
                  height: 'auto',
                  minHeight: '32px',
                  display: 'block',
                  overflow: 'visible',
                  verticalAlign: 'middle'
                } : {}}
                dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}
                lang={companyLanguage === 'ur' ? 'ur' : 'en'}
              />
            </div>

            {/* Date From */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'تاریخ سے:' : 'Date From:'}
              </label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                onClick={(e) => e.target.showPicker && e.target.showPicker()}
                className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs cursor-pointer"
              />
            </div>

            {/* Date To */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'تاریخ تک:' : 'Date To:'}
              </label>
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                onClick={(e) => e.target.showPicker && e.target.showPicker()}
                className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs cursor-pointer"
              />
            </div>

            {/* Supplier Filter */}
            <div>
              <label className={`block text-xs font-medium text-gray-700 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                {companyLanguage === 'ur' ? 'سپلائر:' : 'Supplier:'}
              </label>
              <select
                value={filterSupplier}
                onChange={(e) => setFilterSupplier(e.target.value)}
                className="w-full px-2 py-1 border border-green-300 rounded-lg focus:border-green-500 focus:ring-1 focus:ring-green-200 transition-all text-xs"
              >
                <option value="">{companyLanguage === 'ur' ? 'تمام' : 'All'}</option>
                {uniqueSuppliers.map(supplier => (
                  <option key={supplier} value={supplier}>{supplier}</option>
                ))}
              </select>
            </div>


          </div>
        </div>
      )}

      {/* General Sales Toggle */}
      <div className="mb-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showGeneralSales}
              onChange={(e) => setShowGeneralSales(e.target.checked)}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
            />
            <span className={`text-sm font-semibold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
              {companyLanguage === 'ur' ? 'جنرل سیلز بھی دکھائیں' : 'Show General Sales'}
            </span>
          </label>
          {isLoadingGeneralSales && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          )}
        </div>
        {showGeneralSales && generalSales.length > 0 && (
          <span className={`text-xs text-blue-600 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
            {generalSales.length} {companyLanguage === 'ur' ? 'جنرل سیلز' : 'General Sales'}
          </span>
        )}
      </div>

      {allSales.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>{companyLanguage === 'ur' ? 'ابھی کوئی سیل نہیں' : 'No sales found yet'}</p>
          <p className="text-sm mt-2">
            {companyLanguage === 'ur' ? 'نیا سیل بنانے کے لیے اوپر بٹن دبائیں' : 'Use the button above to create a new sale'}
          </p>
        </div>
      ) : filteredSales.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>{companyLanguage === 'ur' ? 'فلٹر کے مطابق کوئی سیل نہیں ملا' : 'No sales match the filters'}</p>
          <button
            onClick={resetFilters}
            className="mt-4 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-all"
          >
            {companyLanguage === 'ur' ? 'فلٹرز ری سیٹ کریں' : 'Reset Filters'}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Overall Total */}
          {filteredSales.length > 0 && (
            <div className="bg-green-50 border-l-4 border-green-500 rounded-lg py-3 px-4 mb-4">
              <div className="flex justify-between items-center">
                <span className={`text-lg font-bold text-green-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                  {companyLanguage === 'ur' ? 'کل:' : 'Total:'}
                </span>
                <span className={`text-lg font-bold text-green-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                  Rs. {overallTotal.toLocaleString()}
                </span>
              </div>
            </div>
          )}


          {/* Sales grouped by date (Normal View) */}
          {activeViewTab === 'normal' && salesByDate && salesByDate.map((dateGroup) => (
            <div key={dateGroup.date} className="space-y-2 mb-6">
              {/* Date Column Header within the group if needed, but we have a date column in the table */}

              {/* Sale Items Table for this date */}
              <div className="overflow-x-auto rounded-lg border border-gray-300">
                <table className="min-w-full border-collapse" dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                  <thead className="bg-gray-100 border-b-2 border-gray-300">
                    <tr>
                      <th className="px-2 py-2 border border-gray-300 text-center w-10">
                        <input
                          type="checkbox"
                          checked={dateGroup.sales.every(s => selectedSales.includes(s._id))}
                          onChange={() => {
                            const dateIds = dateGroup.sales.map(s => s._id);
                            const allSelected = dateIds.every(id => selectedSales.includes(id));
                            if (allSelected) {
                              setSelectedSales(prev => prev.filter(id => !dateIds.includes(id)));
                            } else {
                              setSelectedSales(prev => [...new Set([...prev, ...dateIds])]);
                            }
                          }}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </th>
                      <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {companyLanguage === 'ur' ? 'سیل #' : 'Sale #'}
                      </th>
                      <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {companyLanguage === 'ur' ? 'تاریخ' : 'Date'}
                      </th>
                      <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {companyLanguage === 'ur' ? 'سپلائر' : 'Supplier'}
                      </th>
                      <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {companyLanguage === 'ur' ? 'کسٹمر' : 'Customer'}
                      </th>
                      <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {companyLanguage === 'ur' ? 'کل رقم' : 'Amount'}
                      </th>
                      <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {companyLanguage === 'ur' ? 'باقی' : 'Remaining'}
                      </th>
                      <th className={`px-3 py-2 border border-gray-300 text-center text-xs font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {companyLanguage === 'ur' ? 'عمل' : 'Action'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {dateGroup.sales.map((sale, idx) => {
                      const isSelected = selectedSales.includes(sale._id);
                      return (
                        <tr
                          key={sale._id}
                          className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-emerald-50 transition-colors ${isSelected ? 'bg-emerald-100' : ''}`}
                        >
                          <td className="px-2 py-2 border border-gray-300 text-center">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleSale(sale._id)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                          <td className={`px-3 py-2 border border-gray-300 text-right text-sm font-semibold text-gray-900 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            {sale.isGeneralSale && (
                              <span className="inline-block bg-blue-100 text-blue-800 text-[10px] px-1 rounded mr-1">
                                {companyLanguage === 'ur' ? 'جنرل' : 'Gen'}
                              </span>
                            )}
                            {sale.serialNumber || sale._id.slice(-6)}
                          </td>
                          <td className="px-3 py-2 border border-gray-300 text-right text-xs text-gray-600">
                            {new Date(sale.date).toLocaleDateString()}
                          </td>
                          <td className={`px-3 py-2 border border-gray-300 text-right text-xs text-gray-600 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            {sale.supplierNameUrdu || sale.supplierName || 'N/A'}
                          </td>
                          <td className={`px-3 py-2 border border-gray-300 text-right text-xs text-gray-600 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            {sale.sellerNameUrdu || sale.sellerName || 'N/A'}
                          </td>
                          <td className="px-3 py-2 border border-gray-300 text-right text-xs font-bold text-green-700">
                            {(parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0).toLocaleString()}
                          </td>
                          <td className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold ${((parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0) - (parseFloat(sale.amountPaid) || 0)) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {((parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0) - (parseFloat(sale.amountPaid) || 0)).toLocaleString()}
                          </td>
                          <td className="px-3 py-2 border border-gray-300 text-center">
                            <div className="flex gap-1 justify-center">
                              {!sale.isGeneralSale ? (
                                <>
                                  <button
                                    onClick={() => navigate(`/sale/view/${sale._id}?lang=${companyLanguage === 'ur' ? 'ur' : 'en'}`)}
                                    className="p-1 bg-brand-header text-white rounded hover:bg-emerald-800 transition-all"
                                    title={companyLanguage === 'ur' ? 'دیکھیں' : 'View'}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                  </button>
                                  <button
                                    onClick={() => handleEditSale && handleEditSale(sale)}
                                    className="p-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-all"
                                    title={companyLanguage === 'ur' ? 'تبدیل کریں' : 'Edit'}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSale && handleDeleteSale(sale._id)}
                                    className="p-1 bg-red-500 text-white rounded hover:bg-red-600 transition-all"
                                    title={companyLanguage === 'ur' ? 'ختم کریں' : 'Delete'}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => navigate('/general-sale')}
                                  className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-all"
                                  title={companyLanguage === 'ur' ? 'دیکھیں' : 'View'}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {/* Sales grouped by bill (Bill Wise View) */}
          {activeViewTab === 'billwise' && salesByBill.map((billGroup) => (
            <div key={billGroup.serialNumber || 'general'} className="space-y-2">
              {/* Bill Header - Center aligned */}
              {billGroup.serialNumber && (
                <div className="bg-gradient-to-r from-blue-50 to-emerald-50 border-l-4 border-blue-500 rounded-lg py-3 px-4">
                  <div className="grid grid-cols-5 gap-4 text-center">
                    <div>
                      <div className={`text-xs text-gray-600 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {companyLanguage === 'ur' ? 'بل نمبر' : 'Bill Number'}
                      </div>
                      <div className={`text-lg font-bold text-blue-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {companyLanguage === 'ur' ? 'بل #' : 'Bill #'}{billGroup.billNumber}
                      </div>
                    </div>
                    <div>
                      <div className={`text-xs text-gray-600 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {companyLanguage === 'ur' ? 'سپلائر' : 'Supplier'}
                      </div>
                      <div className={`text-lg font-bold text-emerald-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {billGroup.supplierNameUrdu || billGroup.supplierName || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className={`text-xs text-gray-600 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {companyLanguage === 'ur' ? 'پختہ بکری' : 'Net Sale'}
                      </div>
                      <div className={`text-lg font-bold text-green-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        Rs. {billGroup.pukhtaBikri.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className={`text-xs text-gray-600 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {companyLanguage === 'ur' ? 'کل رقم' : 'Total Amount'}
                      </div>
                      <div className={`text-lg font-bold text-green-600 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        Rs. {billGroup.total.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div className={`text-xs text-gray-600 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {companyLanguage === 'ur' ? 'کل باقی رقم' : 'Total Remaining'}
                      </div>
                      <div className={`text-lg font-bold ${billGroup.totalRemaining > 0 ? 'text-red-600' : 'text-green-600'} ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        Rs. {billGroup.totalRemaining.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* General Sales Header */}
              {!billGroup.serialNumber && (
                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg py-2 px-3">
                  <div className="flex justify-between items-center">
                    <span className={`text-sm font-bold text-blue-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                      {companyLanguage === 'ur' ? 'جنرل سیلز' : 'General Sales'}
                    </span>
                    <span className={`text-sm font-bold text-blue-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                      {companyLanguage === 'ur' ? 'کل:' : 'Total:'} Rs. {billGroup.total.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}

              {/* Sale Items Table for this bill */}
              <div className="overflow-x-auto rounded-lg border border-gray-300">
                <table className="min-w-full border-collapse" dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                  <thead className="bg-gray-100 border-b-2 border-gray-300">
                    <tr>
                      <th className="px-2 py-2 border border-gray-300 text-center w-10">
                        <input
                          type="checkbox"
                          checked={billGroup.sales.every(s => selectedSales.includes(s._id))}
                          onChange={() => {
                            const billIds = billGroup.sales.map(s => s._id);
                            const allSelected = billIds.every(id => selectedSales.includes(id));
                            if (allSelected) {
                              setSelectedSales(prev => prev.filter(id => !billIds.includes(id)));
                            } else {
                              setSelectedSales(prev => [...new Set([...prev, ...billIds])]);
                            }
                          }}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </th>
                      <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {companyLanguage === 'ur' ? 'سیل #' : 'Sale #'}
                      </th>
                      <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {companyLanguage === 'ur' ? 'تاریخ' : 'Date'}
                      </th>
                      <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {companyLanguage === 'ur' ? 'سپلائر' : 'Supplier'}
                      </th>
                      <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {companyLanguage === 'ur' ? 'کسٹمر' : 'Customer'}
                      </th>
                      <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {companyLanguage === 'ur' ? 'کل رقم' : 'Amount'}
                      </th>
                      <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {companyLanguage === 'ur' ? 'باقی' : 'Remaining'}
                      </th>
                      <th className={`px-3 py-2 border border-gray-300 text-center text-xs font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {companyLanguage === 'ur' ? 'عمل' : 'Action'}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {billGroup.sales.map((sale, idx) => {
                      const isSelected = selectedSales.includes(sale._id);
                      return (
                        <tr
                          key={sale._id}
                          className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-emerald-50 transition-colors ${isSelected ? 'bg-emerald-100' : ''}`}
                        >
                          <td className="px-2 py-2 border border-gray-300 text-center">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleSale(sale._id)}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                          <td className={`px-3 py-2 border border-gray-300 text-right text-sm font-semibold text-gray-900 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            {sale.isGeneralSale && (
                              <span className="inline-block bg-blue-100 text-blue-800 text-[10px] px-1 rounded mr-1">
                                {companyLanguage === 'ur' ? 'جنرل' : 'Gen'}
                              </span>
                            )}
                            {sale.serialNumber || sale._id.slice(-6)}
                          </td>
                          <td className="px-3 py-2 border border-gray-300 text-right text-xs text-gray-600">
                            {new Date(sale.date).toLocaleDateString()}
                          </td>
                          <td className={`px-3 py-2 border border-gray-300 text-right text-xs text-gray-600 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            {sale.supplierNameUrdu || sale.supplierName || 'N/A'}
                          </td>
                          <td className={`px-3 py-2 border border-gray-300 text-right text-xs text-gray-600 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            {sale.sellerNameUrdu || sale.sellerName || 'N/A'}
                          </td>
                          <td className="px-3 py-2 border border-gray-300 text-right text-xs font-bold text-green-700">
                            {(parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0).toLocaleString()}
                          </td>
                          <td className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold ${((parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0) - (parseFloat(sale.amountPaid) || 0)) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {((parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0) - (parseFloat(sale.amountPaid) || 0)).toLocaleString()}
                          </td>
                          <td className="px-3 py-2 border border-gray-300 text-center">
                            <div className="flex gap-1 justify-center">
                              {!sale.isGeneralSale ? (
                                <>
                                  <button
                                    onClick={() => navigate(`/sale/view/${sale._id}?lang=${companyLanguage === 'ur' ? 'ur' : 'en'}`)}
                                    className="p-1 bg-brand-header text-white rounded hover:bg-emerald-800 transition-all"
                                    title={companyLanguage === 'ur' ? 'دیکھیں' : 'View'}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                  </button>
                                  <button
                                    onClick={() => handleEditSale && handleEditSale(sale)}
                                    className="p-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-all"
                                    title={companyLanguage === 'ur' ? 'تبدیل کریں' : 'Edit'}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSale && handleDeleteSale(sale._id)}
                                    className="p-1 bg-red-500 text-white rounded hover:bg-red-600 transition-all"
                                    title={companyLanguage === 'ur' ? 'ختم کریں' : 'Delete'}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => navigate('/general-sale')}
                                  className="p-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-all"
                                  title={companyLanguage === 'ur' ? 'دیکھیں' : 'View'}
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {/* Sales grouped by supplier (Supplier Wise View) */}
          {activeViewTab === 'supplierwise' && salesBySupplier.map((supplierGroup) => (
            <div key={supplierGroup.supplierName || supplierGroup.supplierNameUrdu} className="space-y-4 mb-6">
              {/* Supplier Header */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-purple-500 rounded-lg py-3 px-4">
                <div className={`text-xl font-bold text-purple-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                  {supplierGroup.supplierNameUrdu || supplierGroup.supplierName || 'Unknown Supplier'}
                </div>
              </div>

              {/* Bills for this supplier */}
              {supplierGroup.bills.map((billGroup) => (
                <div key={billGroup.serialNumber || billGroup.date} className="space-y-2 ml-4">
                  {/* Bill Header */}
                  <div className="bg-gradient-to-r from-blue-50 to-emerald-50 border-l-4 border-blue-500 rounded-lg py-3 px-4">
                    <div className="grid grid-cols-5 gap-4 text-center">
                      <div>
                        <div className={`text-xs text-gray-600 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                          {companyLanguage === 'ur' ? 'بل نمبر' : 'Bill Number'}
                        </div>
                        <div className={`text-lg font-bold text-blue-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                          {companyLanguage === 'ur' ? 'بل #' : 'Bill #'}{billGroup.billNumber}
                        </div>
                      </div>
                      <div>
                        <div className={`text-xs text-gray-600 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                          {companyLanguage === 'ur' ? 'تاریخ' : 'Date'}
                        </div>
                        <div className={`text-lg font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                          {new Date(billGroup.date).toLocaleDateString()}
                        </div>
                      </div>
                      <div>
                        <div className={`text-xs text-gray-600 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                          {companyLanguage === 'ur' ? 'پختہ بکری' : 'Net Sale'}
                        </div>
                        <div className={`text-lg font-bold text-green-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                          Rs. {billGroup.pukhtaBikri.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className={`text-xs text-gray-600 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                          {companyLanguage === 'ur' ? 'کل رقم' : 'Total Amount'}
                        </div>
                        <div className={`text-lg font-bold text-green-600 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                          Rs. {billGroup.total.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className={`text-xs text-gray-600 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                          {companyLanguage === 'ur' ? 'کل باقی رقم' : 'Total Remaining'}
                        </div>
                        <div className={`text-lg font-bold ${billGroup.totalRemaining > 0 ? 'text-red-600' : 'text-green-600'} ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                          Rs. {billGroup.totalRemaining.toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sale Items Table for this bill */}
                  <div className="overflow-x-auto rounded-lg border border-gray-300 ml-4">
                    <table className="min-w-full border-collapse" dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                      <thead className="bg-gray-100 border-b-2 border-gray-300">
                        <tr>
                          <th className="px-2 py-2 border border-gray-300 text-center w-10">
                            <input
                              type="checkbox"
                              checked={billGroup.sales.every(s => selectedSales.includes(s._id))}
                              onChange={() => {
                                const ids = billGroup.sales.map(s => s._id);
                                const allSelected = ids.every(id => selectedSales.includes(id));
                                if (allSelected) setSelectedSales(prev => prev.filter(id => !ids.includes(id)));
                                else setSelectedSales(prev => [...new Set([...prev, ...ids])]);
                              }}
                              className="w-4 h-4 cursor-pointer"
                            />
                          </th>
                          <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            {companyLanguage === 'ur' ? 'سیل #' : 'Sale #'}
                          </th>
                          <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            {companyLanguage === 'ur' ? 'تاریخ' : 'Date'}
                          </th>
                          <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            {companyLanguage === 'ur' ? 'کسٹمر' : 'Customer'}
                          </th>
                          <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            {companyLanguage === 'ur' ? 'رقم' : 'Amount'}
                          </th>
                          <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            {companyLanguage === 'ur' ? 'باقی' : 'Remaining'}
                          </th>
                          <th className={`px-3 py-2 border border-gray-300 text-center text-xs font-bold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            {companyLanguage === 'ur' ? 'عمل' : 'Action'}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {billGroup.sales.map((sale, idx) => {
                          const isSelected = selectedSales.includes(sale._id);
                          return (
                            <tr
                              key={sale._id}
                              className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-emerald-50 transition-colors ${isSelected ? 'bg-emerald-100' : ''}`}
                            >
                              <td className="px-2 py-2 border border-gray-300 text-center">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleToggleSale(sale._id)}
                                  className="w-4 h-4 cursor-pointer"
                                />
                              </td>
                              <td className={`px-3 py-2 border border-gray-300 text-right text-sm font-semibold text-gray-900 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                                {sale.serialNumber || sale._id.slice(-6)}
                              </td>
                              <td className="px-3 py-2 border border-gray-300 text-right text-xs text-gray-600">
                                {new Date(sale.date).toLocaleDateString()}
                              </td>
                              <td className={`px-3 py-2 border border-gray-300 text-right text-xs text-gray-600 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                                {sale.sellerNameUrdu || sale.sellerName || 'N/A'}
                              </td>
                              <td className="px-3 py-2 border border-gray-300 text-right text-xs font-bold text-green-700">
                                {(parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0).toLocaleString()}
                              </td>
                              <td className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold ${((parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0) - (parseFloat(sale.amountPaid) || 0)) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                {((parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0) - (parseFloat(sale.amountPaid) || 0)).toLocaleString()}
                              </td>
                              <td className="px-3 py-2 border border-gray-300 text-center">
                                <div className="flex gap-1 justify-center">
                                  <button
                                    onClick={() => navigate(`/sale/view/${sale._id}?lang=${companyLanguage === 'ur' ? 'ur' : 'en'}`)}
                                    className="p-1 bg-brand-header text-white rounded hover:bg-emerald-800 transition-all"
                                    title={companyLanguage === 'ur' ? 'دیکھیں' : 'View'}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                  </button>
                                  <button
                                    onClick={() => handleEditSale && handleEditSale(sale)}
                                    className="p-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-all"
                                    title={companyLanguage === 'ur' ? 'تبدیل کریں' : 'Edit'}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteSale && handleDeleteSale(sale._id)}
                                    className="p-1 bg-red-500 text-white rounded hover:bg-red-600 transition-all"
                                    title={companyLanguage === 'ur' ? 'ختم کریں' : 'Delete'}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ))}

          {/* Sales grouped by customer (Customer Wise View) */}
          {activeViewTab === 'customerwise' && salesByCustomer.map((customerGroup) => {
            // Get selected sales for this customer
            const customerSales = customerGroup.bills.flatMap(bill => bill.sales);
            const selectedCustomerSales = customerSales.filter(sale => selectedSales.includes(sale._id));

            // Print function for this customer
            const handlePrintCustomerSales = () => {
              if (selectedCustomerSales.length === 0) {
                toast.warning(companyLanguage === 'ur' ? 'براہ کرم کم از کم ایک سیل منتخب کریں' : 'Please select at least one sale');
                return;
              }
              window.print();
            };

            return (
              <div key={customerGroup.customerName || customerGroup.customerNameUrdu} className="space-y-4 mb-6">
                {/* Customer Header - Hidden in print */}
                <div className="bg-gradient-to-r from-indigo-50 to-cyan-50 border-l-4 border-indigo-500 rounded-lg py-3 px-4 print:hidden">
                  <div className="flex justify-between items-center">
                    <div className={`text-xl font-bold text-indigo-700 ${companyLanguage === 'ur' ? 'font-urdu text-right' : 'text-left'}`}>
                      {customerGroup.customerNameUrdu || customerGroup.customerName || 'Unknown Customer'}
                    </div>
                    {selectedCustomerSales.length > 0 && (
                      <button
                        onClick={handlePrintCustomerSales}
                        className={`px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded transition-all ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                      >
                        {companyLanguage === 'ur' ? `پرنٹ (${selectedCustomerSales.length})` : `Print (${selectedCustomerSales.length})`}
                      </button>
                    )}
                  </div>
                </div>

                {/* Print Section - Hidden except when printing */}
                {selectedCustomerSales.length > 0 && (
                  <div ref={printRef} className="hidden print:block print:bg-white print:p-0" style={{ marginTop: 0, paddingTop: 0 }}>
                    {/* Customer Name - Top */}
                    <div className="print:text-center print:mb-4" style={{ marginTop: 0, paddingTop: 0 }}>
                      <h1 className={`print:text-xl print:font-bold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                        {customerGroup.customerNameUrdu || customerGroup.customerName || 'Unknown Customer'}
                      </h1>
                    </div>

                    {/* Detailed Items Table (Tafseeli Akhrajat) - All selected sales items */}
                    <div className="print:mb-4">
                      <h3 className={`print:text-md print:font-bold print:mb-2 print:text-right ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                        {companyLanguage === 'ur' ? 'خریداری کی تفصیل' : 'Purchase Details'}
                      </h3>
                      <table className="print:w-full print:border-collapse print:border print:border-gray-400">
                        <thead>
                          <tr className="print:bg-gray-100">
                            <th className={`print:border print:border-gray-400 print:px-2 print:py-2 print:text-sm print:font-bold print:text-right ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                              {companyLanguage === 'ur' ? 'تفصیل' : 'Description'}
                            </th>
                            <th className={`print:border print:border-gray-400 print:px-2 print:py-2 print:text-sm print:font-bold print:text-right ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                              {companyLanguage === 'ur' ? 'یونٹ' : 'Unit'}
                            </th>
                            <th className={`print:border print:border-gray-400 print:px-2 print:py-2 print:text-sm print:font-bold print:text-right ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                              {companyLanguage === 'ur' ? 'دانہ' : 'Dana'}
                            </th>
                            <th className={`print:border print:border-gray-400 print:px-2 print:py-2 print:text-sm print:font-bold print:text-right ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                              {companyLanguage === 'ur' ? 'مقدار' : 'Quantity'}
                            </th>
                            <th className={`print:border print:border-gray-400 print:px-2 print:py-2 print:text-sm print:font-bold print:text-right ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                              {companyLanguage === 'ur' ? 'فی یونٹ قیمت' : 'Per Unit Price'}
                            </th>
                            <th className={`print:border print:border-gray-400 print:px-2 print:py-2 print:text-sm print:font-bold print:text-right ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                              {companyLanguage === 'ur' ? 'کل رقم' : 'Total Amount'}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedCustomerSales.flatMap((sale) =>
                            (sale.items || []).map((item, itemIndex) => (
                              <tr key={`${sale._id}-${itemIndex}`}>
                                <td className={`print:border print:border-gray-400 print:px-2 print:py-2 print:text-sm print:text-right ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                                  {companyLanguage === 'ur'
                                    ? (item.descriptionUrdu || item.description || '-')
                                    : (item.description || item.descriptionUrdu || '-')}
                                </td>
                                <td className={`print:border print:border-gray-400 print:px-2 print:py-2 print:text-sm print:text-right ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                                  {item.unit || '-'}
                                </td>
                                <td className={`print:border print:border-gray-400 print:px-2 print:py-2 print:text-sm print:text-center ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                                  {item.dana || '-'}
                                </td>
                                <td className={`print:border print:border-gray-400 print:px-2 print:py-2 print:text-sm print:text-center ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                                  {item.quantity || 0}
                                </td>
                                <td className={`print:border print:border-gray-400 print:px-2 print:py-2 print:text-sm print:text-center ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                                  {(item.ratePerUnit || 0).toLocaleString()}
                                </td>
                                <td className={`print:border print:border-gray-400 print:px-2 print:py-2 print:text-sm print:text-center print:font-semibold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                                  {(item.lineTotal || 0).toLocaleString()}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Summary Section - Bottom Right */}
                    <div className="print:mt-4 print:flex print:justify-end">
                      <div className={`print:space-y-2 print:text-right ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                        {(() => {
                          const totalAmount = selectedCustomerSales.reduce((sum, sale) =>
                            sum + (parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0), 0
                          );
                          const totalPaid = selectedCustomerSales.reduce((sum, sale) =>
                            sum + (parseFloat(sale.amountPaid) || 0), 0
                          );
                          const totalRemaining = totalAmount - totalPaid;

                          return (
                            <>
                              <div className={`print:text-sm print:font-semibold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                                {companyLanguage === 'ur' ? 'ذیلی کل:' : 'Subtotal:'} {totalAmount.toLocaleString()}
                              </div>
                              <div className={`print:text-sm print:font-semibold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                                {companyLanguage === 'ur' ? 'کل رقم :' : 'Total Amount :'} {totalAmount.toLocaleString()}
                              </div>
                              <div className={`print:text-sm print:font-semibold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                                {companyLanguage === 'ur' ? 'ادا کی گئی رقم :' : 'Amount Paid :'} {totalPaid.toLocaleString()}
                              </div>
                              <div className={`print:text-sm print:font-bold ${totalRemaining > 0 ? 'print:text-red-600' : 'print:text-green-600'} ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                                {companyLanguage === 'ur' ? 'بقایا :' : 'Balance :'} {totalRemaining.toLocaleString()}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}

                {/* Bills for this customer - Hidden in print */}
                {customerGroup.bills.map((billGroup) => (
                  <div key={billGroup.serialNumber || billGroup.date} className="space-y-2 ml-4 print:hidden">
                    {/* Bill Header */}
                    <div className="bg-gradient-to-r from-blue-50 to-emerald-50 border-l-4 border-blue-500 rounded-lg py-3 px-4">
                      <div className="grid grid-cols-5 gap-4 text-center">
                        <div>
                          <div className={`text-xs text-gray-600 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            {companyLanguage === 'ur' ? 'بل نمبر' : 'Bill Number'}
                          </div>
                          <div className={`text-lg font-bold text-blue-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            {companyLanguage === 'ur' ? 'بل #' : 'Bill #'}{billGroup.billNumber}
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs text-gray-600 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            {companyLanguage === 'ur' ? 'تاریخ' : 'Date'}
                          </div>
                          <div className={`text-lg font-bold text-gray-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            {new Date(billGroup.date).toLocaleDateString()}
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs text-gray-600 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            {companyLanguage === 'ur' ? 'پختہ بکری' : 'Net Sale'}
                          </div>
                          <div className={`text-lg font-bold text-green-700 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            Rs. {billGroup.pukhtaBikri.toLocaleString()}
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs text-gray-600 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            {companyLanguage === 'ur' ? 'کل رقم' : 'Total Amount'}
                          </div>
                          <div className={`text-lg font-bold text-green-600 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            Rs. {billGroup.total.toLocaleString()}
                          </div>
                        </div>
                        <div>
                          <div className={`text-xs text-gray-600 mb-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            {companyLanguage === 'ur' ? 'کل باقی رقم' : 'Total Remaining'}
                          </div>
                          <div className={`text-lg font-bold ${billGroup.totalRemaining > 0 ? 'text-red-600' : 'text-green-600'} ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                            Rs. {billGroup.totalRemaining.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Sale Items Table for this bill */}
                    <div className="overflow-x-auto rounded-lg border border-gray-300 ml-4">
                      <table className="min-w-full border-collapse" dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}>
                        <thead className="bg-gray-100 border-b-2 border-gray-300">
                          <tr>
                            <th className="px-2 py-2 border border-gray-300 text-center w-10">
                              <input
                                type="checkbox"
                                checked={billGroup.sales.every(s => selectedSales.includes(s._id))}
                                onChange={() => {
                                  const ids = billGroup.sales.map(s => s._id);
                                  const allSelected = ids.every(id => selectedSales.includes(id));
                                  if (allSelected) setSelectedSales(prev => prev.filter(id => !ids.includes(id)));
                                  else setSelectedSales(prev => [...new Set([...prev, ...ids])]);
                                }}
                                className="w-4 h-4 cursor-pointer"
                              />
                            </th>
                            <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                              {companyLanguage === 'ur' ? 'سیل #' : 'Sale #'}
                            </th>
                            <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                              {companyLanguage === 'ur' ? 'تاریخ' : 'Date'}
                            </th>
                            <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                              {companyLanguage === 'ur' ? 'سپلائر' : 'Supplier'}
                            </th>
                            <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                              {companyLanguage === 'ur' ? 'رقم' : 'Amount'}
                            </th>
                            <th className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                              {companyLanguage === 'ur' ? 'باقی' : 'Remaining'}
                            </th>
                            <th className={`px-3 py-2 border border-gray-300 text-center text-xs font-bold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                              {companyLanguage === 'ur' ? 'عمل' : 'Action'}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {billGroup.sales.map((sale, idx) => {
                            const isSelected = selectedSales.includes(sale._id);
                            return (
                              <tr
                                key={sale._id}
                                className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-emerald-50 transition-colors ${isSelected ? 'bg-emerald-100' : ''}`}
                              >
                                <td className="px-2 py-2 border border-gray-300 text-center">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleToggleSale(sale._id)}
                                    className="w-4 h-4 cursor-pointer"
                                  />
                                </td>
                                <td className={`px-3 py-2 border border-gray-300 text-right text-sm font-semibold text-gray-900 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                                  {sale.serialNumber || sale._id.slice(-6)}
                                </td>
                                <td className="px-3 py-2 border border-gray-300 text-right text-xs text-gray-600">
                                  {new Date(sale.date).toLocaleDateString()}
                                </td>
                                <td className={`px-3 py-2 border border-gray-300 text-right text-xs text-gray-600 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}>
                                  {sale.supplierNameUrdu || sale.supplierName || 'N/A'}
                                </td>
                                <td className="px-3 py-2 border border-gray-300 text-right text-xs font-bold text-green-700">
                                  {(parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0).toLocaleString()}
                                </td>
                                <td className={`px-3 py-2 border border-gray-300 text-right text-xs font-bold ${((parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0) - (parseFloat(sale.amountPaid) || 0)) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                  {((parseFloat(sale.totalAmount) || parseFloat(sale.total) || 0) - (parseFloat(sale.amountPaid) || 0)).toLocaleString()}
                                </td>
                                <td className="px-3 py-2 border border-gray-300 text-center">
                                  <div className="flex gap-1 justify-center">
                                    <button
                                      onClick={() => navigate(`/sale/view/${sale._id}?lang=${companyLanguage === 'ur' ? 'ur' : 'en'}`)}
                                      className="p-1 bg-brand-header text-white rounded hover:bg-emerald-800 transition-all"
                                      title={companyLanguage === 'ur' ? 'دیکھیں' : 'View'}
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                    </button>
                                    <button
                                      onClick={() => handleEditSale && handleEditSale(sale)}
                                      className="p-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-all"
                                      title={companyLanguage === 'ur' ? 'تبدیل کریں' : 'Edit'}
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteSale && handleDeleteSale(sale._id)}
                                      className="p-1 bg-red-500 text-white rounded hover:bg-red-600 transition-all"
                                      title={companyLanguage === 'ur' ? 'ختم کریں' : 'Delete'}
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            margin: 0 !important;
            padding: 0 !important;
          }
          body {
            margin: 0 !important;
            padding: 0 !important;
          }
          .print\\:p-0 {
            padding: 0 !important;
            margin: 0 !important;
          }
          [ref] {
            margin-top: 0 !important;
            padding-top: 0 !important;
          }
        }
      `}</style>

      <UrduKeyboard
        isVisible={showSearchUrduKeyboard}
        onKeyPress={handleSearchUrduKeyPress}
        onClose={() => setShowSearchUrduKeyboard(false)}
      />
    </div>
  );
});

SalesTab.displayName = 'SalesTab';

export default SalesTab;


